import { useState, useEffect, useCallback } from "react";
import { CheckCircle2, Lock, Database, History } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { productApi } from "@/lib/api";
import { ProductModeBanner } from "@/components/ProductModeBanner";

const SOURCE_META = {
  bright: { title: "Web-indexing (Bright)", sourceKey: "web_indexing",
    getSettings: productApi.getBrightSettings, getLogs: () => productApi.getBrightSyncLogs(20), hasBrowser: true },
  aniview: { title: "FAST TV Ads (Aniview)", sourceKey: "fast_tv_ads",
    getSettings: productApi.getAniviewSettings, getLogs: productApi.getAniviewSyncLogs, hasBrowser: false },
  springserve: { title: "SpringServe", sourceKey: "springserve",
    getSettings: productApi.getSpringserveSettings, getLogs: productApi.getSpringserveSyncLogs, hasBrowser: false },
};

function StatusBadge({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${ok ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
      {ok ? <CheckCircle2 size={12} /> : <Lock size={12} />} {label}
    </span>
  );
}

export default function ProductSourcePage({ source }) {
  const meta = SOURCE_META[source];
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const LIMIT = 25;

  useEffect(() => {
    meta.getSettings().then(r => setSettings(r.data)).catch(() => setSettings({}));
    meta.getLogs().then(r => setLogs(r.data || [])).catch(() => setLogs([]));
  }, [source]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBrowser = useCallback(() => {
    if (!meta.hasBrowser) return;
    productApi.getBrightDataBrowser({ source: meta.sourceKey, page, limit: LIMIT })
      .then(r => { setRecords(r.data.records || []); setTotal(r.data.total || 0); })
      .catch(() => { setRecords([]); setTotal(0); });
  }, [source, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchBrowser(); }, [fetchBrowser]);

  const configured = settings && (settings.api_key_set || settings.password_set);

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid={`product-source-${source}`}>
      <ProductModeBanner />
      <p className="text-xs text-slate-500" data-testid="product-source-readonly-note">Read-only view — credentials are masked and syncs are run by your admin.</p>

      <h1 className="text-xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {meta.title}
      </h1>

      {/* Connection status + masked config */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3" data-testid="product-source-config">
        <div className="flex items-center gap-2">
          <Lock size={16} className="text-slate-400" />
          <h3 className="text-sm font-semibold text-slate-900">Connection</h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge ok={!!configured} label={configured ? "Configured" : "Not configured"} />
          {"api_key_set" in (settings || {}) && <StatusBadge ok={settings.api_key_set} label="API key" />}
          {"password_set" in (settings || {}) && <StatusBadge ok={settings.password_set} label="Password" />}
          <StatusBadge ok={!!settings?.daily_sync_enabled} label={settings?.daily_sync_enabled ? "Daily sync on" : "Daily sync off"} />
        </div>
        {settings?.email && <p className="text-xs text-slate-500">Account: <span className="font-mono text-slate-700">{settings.email}</span></p>}
        <p className="text-[11px] text-slate-400">Actual API keys and passwords are hidden by design — only their presence is shown.</p>
      </div>

      {/* Sync history */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="product-source-logs">
        <div className="p-4 border-b border-slate-100 flex items-center gap-2">
          <History size={16} className="text-slate-400" />
          <h3 className="text-sm font-semibold text-slate-900">Sync History</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Type</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Range</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3 text-right">Inserted</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3 text-right">Updated</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">No sync history</TableCell></TableRow>
            ) : logs.map((l, i) => (
              <TableRow key={i} className="border-b border-slate-100" data-testid={`product-log-row-${i}`}>
                <TableCell className="py-2 px-3 text-sm text-slate-600">{l.sync_type}</TableCell>
                <TableCell className="py-2 px-3 text-sm text-slate-600">{l.start_date} → {l.end_date}</TableCell>
                <TableCell className="py-2 px-3 text-sm text-right font-mono text-slate-700">{l.rows_inserted}</TableCell>
                <TableCell className="py-2 px-3 text-sm text-right font-mono text-slate-700">{l.rows_updated}</TableCell>
                <TableCell className="py-2 px-3">
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{l.status}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Bright data browser */}
      {meta.hasBrowser && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="product-data-browser">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database size={16} className="text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-900">Data Browser</h3>
            </div>
            <span className="text-xs text-slate-400">{total} records</span>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Date</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Identifier</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3 text-right">Earnings</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3 text-right">Peers</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-400">No records</TableCell></TableRow>
              ) : records.map((r, i) => (
                <TableRow key={i} className="border-b border-slate-100" data-testid={`product-browser-row-${i}`}>
                  <TableCell className="py-2 px-3 text-sm text-slate-700">{r.ymd}</TableCell>
                  <TableCell className="py-2 px-3 text-sm font-medium text-slate-900">{r.campaign}</TableCell>
                  <TableCell className="py-2 px-3 text-sm text-right font-mono text-slate-700">{r.earnings === null ? <span className="text-slate-400 italic font-sans text-xs" data-testid="browser-earnings-unavailable">Unavailable</span> : `$${Number(r.earnings || 0).toFixed(2)}`}</TableCell>
                  <TableCell className="py-2 px-3 text-sm text-right font-mono text-slate-700">{(r.peers || 0).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {total > LIMIT && (
            <div className="flex items-center justify-between p-3" data-testid="product-browser-pagination">
              <span className="text-xs text-slate-500">Page {page} / {Math.ceil(total / LIMIT)}</span>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)} className="h-8 w-8 p-0" data-testid="product-browser-prev"><ChevronLeft size={16} /></Button>
                <Button variant="outline" size="sm" disabled={page * LIMIT >= total} onClick={() => setPage(page + 1)} className="h-8 w-8 p-0" data-testid="product-browser-next"><ChevronRight size={16} /></Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
